import { CARE_CALL_LANGUAGES, readNumberDetail, type MemberContactMode } from '@galapagos/shared';
import type {
  AddressSuggestionsView,
  BookCareAppointmentRequest,
  CareAppointmentData,
  CareAppointmentSlotsData,
  CareAppointmentSlotsQuery,
  CheckInBatchRequest,
  CheckInData,
  CompanySuggestionsView,
  ConsentDocumentPreviewData,
  ConsentDocumentTypeParam,
  EligibilityCheckRequest,
  EligibilityCheckResponse,
  EligibilityStatusView,
  HdhpNoticeData,
  OnboardingSessionData,
  OnboardingUsageConfig,
  RegistrationProfileSaveView,
  RegistrationProfileView,
  SignupResumeView,
  RegistrationProfileWrite,
} from '@galapagos/shared';
import type { SamlContact } from '@galapagos/shared';
import type { OtpChannel, OtpInitiateData } from '@galapagos/shared/api/auth';
import type {
  SignupBaselineContent,
  SignupProgramOptions,
  SignupQuizContent,
} from '@galapagos/shared/registration';
import type { ApiError, ApiErrorKind } from '@/services/api';
import type { SessionTokens } from '@/services/session';
import type { DownloadCampaign, DownloadPlacement } from '../../download/download-links';

/**
 * Every call the rebuilt signup flow makes, behind one seam (ADR-053). Screens
 * call these and nothing else, so the local fake and the real backend are
 * interchangeable, and a backend slice that lands later only replaces a
 * not-implemented method in the real implementation.
 *
 * Each method resolves with the owner schema's parsed type, or rejects with a
 * `SignupNotImplementedError` or a `SignupRequestError`.
 */
/** Narrows the employer search to one parent company's sub-employers (bare company id). */
export interface EmployerSearchScope {
  parentRef: string;
}

/**
 * A verified code: the session, and whether the contact belongs to a Pelago
 * 1.0 member who has not finished moving to Galapagos. That member already
 * has an account, so the flow sends them to the app instead of signing up.
 */
export type SignupVerifiedSession = SessionTokens & { migrationStartRequired: boolean };

export interface SignupApi {
  resolveSponsor(input: SignupSponsorQuery): Promise<SignupSponsor>;
  /** What the substance step offers, for every language; filtered by the sponsor's keys. */
  getProgramOptions(): Promise<SignupProgramOptions>;
  initiateOtp(input: SignupOtpInitiate): Promise<OtpInitiateData>;
  /** Resolves with the session; the caller signs the member in, as today. */
  verifyOtp(input: SignupOtpVerify): Promise<SignupVerifiedSession>;
  getRegistrationProfile(): Promise<RegistrationProfileView>;
  /** The signed-in member's contacts, to rebuild the sign-in contact after a reload lost it. */
  getSignedInContact(): Promise<SignupSignedInContact>;
  /**
   * The name and employer reference from the signed-in member's own latest
   * eligibility check, for welcoming back a member who left before saving
   * their details. All null when there is none.
   */
  getSignupResume(): Promise<SignupResumeView>;
  submitEligibility(request: EligibilityCheckRequest): Promise<EligibilityCheckResponse>;
  getEligibilityStatus(): Promise<EligibilityStatusView>;
  /** The company directory typeahead, or one parent company's sub-employers when scoped. */
  searchEmployers(query: string, scope?: EmployerSearchScope): Promise<CompanySuggestionsView>;
  suggestAddresses(query: string): Promise<AddressSuggestionsView>;
  /** The completion write; `firstCompletion` is true only on the save that completed registration. */
  saveRegistrationProfile(write: RegistrationProfileWrite): Promise<RegistrationProfileSaveView>;
  getHdhpNotice(): Promise<HdhpNoticeData>;
  /** The interpreter the member wants on their care calls; best effort, a rejection never blocks booking. */
  saveLanguagePreference(input: SignupLanguagePreferenceSave): Promise<void>;
  /** Whether the member chose a welcome call or chat in the app; best effort, a rejection never blocks the flow. */
  saveContactMode(input: SignupContactModeSave): Promise<void>;
  /**
   * For a sponsor that offers Spanish: the language the member signed up in, as
   * their call language. Never replaces one the member picked; best effort.
   */
  saveAppLanguageDefault(input: SignupAppLanguageDefaultSave): Promise<void>;
  previewConsentDocument(
    consentType: ConsentDocumentTypeParam,
  ): Promise<ConsentDocumentPreviewData>;
  captureConsent(input: SignupConsentCapture): Promise<void>;
  /** The signup quiz's question content for every language; served content, like the program options. */
  getQuizContent(): Promise<SignupQuizContent>;
  getOnboardingSession(flowId: string): Promise<OnboardingSessionData>;
  answerOnboarding(flowId: string, body: SignupOnboardingAnswers): Promise<OnboardingSessionData>;
  /** Which programs have their own last-week wording, for every language; picked by the member's primary program. */
  getBaselineContent(): Promise<SignupBaselineContent>;
  /** Each program's usage capture (counter or yes/no), as the app reads it; picked by the member's primary program. */
  getOnboardingUsageConfig(): Promise<OnboardingUsageConfig>;
  submitCheckIns(request: CheckInBatchRequest): Promise<CheckInData[]>;
  getBookingSlots(query?: CareAppointmentSlotsQuery): Promise<CareAppointmentSlotsData>;
  /** The live landing attribution is attached by the implementation, not the caller. */
  bookAppointment(request: SignupBookingRequest): Promise<CareAppointmentData>;
  getDownloadHandoff(request?: SignupDownloadHandoffRequest): Promise<SignupDownloadHandoff>;
}

/** The method names, in interface order. Tests hold both implementations to it. */
export const SIGNUP_API_METHODS = [
  'resolveSponsor',
  'getProgramOptions',
  'initiateOtp',
  'verifyOtp',
  'getRegistrationProfile',
  'getSignedInContact',
  'getSignupResume',
  'submitEligibility',
  'getEligibilityStatus',
  'searchEmployers',
  'suggestAddresses',
  'saveRegistrationProfile',
  'getHdhpNotice',
  'saveLanguagePreference',
  'saveContactMode',
  'saveAppLanguageDefault',
  'previewConsentDocument',
  'captureConsent',
  'getQuizContent',
  'getOnboardingSession',
  'answerOnboarding',
  'getBaselineContent',
  'getOnboardingUsageConfig',
  'submitCheckIns',
  'getBookingSlots',
  'bookAppointment',
  'getDownloadHandoff',
] as const satisfies readonly (keyof SignupApi)[];

export type SignupApiMethod = (typeof SIGNUP_API_METHODS)[number];

export interface SignupSponsorQuery {
  clientId?: string;
  subClientId?: string;
}

/**
 * What the screens need to know about the sponsor a landing link names, as
 * `GET /api/v1/eligibility/sponsors/{clientId}` answers it. Program keys stay
 * opaque strings.
 */
export interface SignupSponsor {
  clientRef: string;
  subClientRef?: string;
  displayName: string;
  programKeys: string[];
  features: SignupSponsorFeatures;
}

export interface SignupSponsorFeatures {
  minorSupport: boolean;
  spanish: boolean;
  callChoice: boolean;
  healthPlan: boolean;
  multipleProgramSelection: boolean;
  rewards: boolean;
  /** Ask whether the member wants an interpreter before booking a call. */
  languagePreference: boolean;
  /** Whether members book an onboarding call at the end; absent means they do. */
  onboardingCall?: boolean;
}

/**
 * Where a code goes: a destination the member typed, or a roster contact the
 * server unseals from an employer SSO session. The browser never learns the
 * unmasked roster contact.
 */
export type SignupOtpInitiate =
  | { channel: OtpChannel; destination: string }
  | { saml: { session: string; contact: SamlContact } };

export type SignupOtpVerify = SignupOtpInitiate & {
  code: string;
  /** New signups only; returning members omit it. */
  consent?: { termsConsent: boolean; hipaaConsent: boolean };
};

/**
 * The contacts on the signed-in member's record (`GET /api/v1/auth/me`): the
 * one they signed in with, and after the profile save the other one too.
 */
export interface SignupSignedInContact {
  email: string | null;
  /** E.164. */
  phone: string | null;
}

/** The interpreter choices the signup offers; `en` means no interpreter. The shared vocabulary the API stores. */
export const SIGNUP_LANGUAGE_PREFERENCES = CARE_CALL_LANGUAGES;

export type SignupLanguagePreference = (typeof SIGNUP_LANGUAGE_PREFERENCES)[number];

export interface SignupLanguagePreferenceSave {
  language: SignupLanguagePreference;
}

export interface SignupContactModeSave {
  contactMode: MemberContactMode;
}

/** The signup is shown in English or Spanish, so only those can be its language. */
export interface SignupAppLanguageDefaultSave {
  language: 'en' | 'es';
}

export interface SignupConsentCapture {
  smsOptIn: boolean;
  part2Release: boolean;
}

export interface SignupOnboardingAnswers {
  answers: Record<string, unknown>;
  finalize?: boolean;
}

export type SignupBookingRequest = Omit<BookCareAppointmentRequest, 'attribution'>;

/**
 * Where the links are shown, and the campaign they carry. Without a campaign
 * the member's stored landing campaign is used; the direct-download link
 * passes its own, so an earlier landing's UTMs never label it.
 */
export interface SignupDownloadHandoffRequest {
  placement?: DownloadPlacement;
  campaign?: DownloadCampaign;
}

export interface SignupDownloadHandoff {
  appStoreUrl: string;
  playStoreUrl: string;
  /** The one link a phone camera scans from the desktop QR; it opens that phone's store. */
  scanUrl: string;
}

/** A call whose backend does not exist yet. A backend slice replaces it. */
export class SignupNotImplementedError extends Error {
  readonly method: SignupApiMethod;

  constructor(method: SignupApiMethod) {
    super(`SignupApi.${method} has no backend yet`);
    this.name = 'SignupNotImplementedError';
    this.method = method;
  }
}

/**
 * A call that reached the transport and failed. Carries what screens branch on:
 * the transport `kind` and HTTP status for retry decisions, the server's
 * machine-readable `code` for a specific message, and the server's wait hint on
 * a 429.
 */
export class SignupRequestError extends Error {
  readonly kind?: ApiErrorKind;
  readonly httpStatus?: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    details: {
      kind?: ApiErrorKind;
      httpStatus?: number;
      code?: string;
      retryAfterSeconds?: number;
    } = {},
  ) {
    super(message);
    this.name = 'SignupRequestError';
    this.kind = details.kind;
    this.httpStatus = details.httpStatus;
    this.code = details.code;
    this.retryAfterSeconds = details.retryAfterSeconds;
  }

  static fromApiError(error: ApiError): SignupRequestError {
    return new SignupRequestError(error.message, {
      kind: error.kind,
      httpStatus: error.httpStatus,
      code: error.code,
      retryAfterSeconds: readNumberDetail(error.details, 'retryAfterSeconds'),
    });
  }
}
